import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { autorun, comparer } from 'mobx';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { merge, updateSearch } from 'bernie-core';

import { DynamicSearchMap } from '@shared-ui/lodging-property-search';
import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { PROPERTY_LISTING_BATCH_KEY } from 'src/common/constants/batchKeys';

import { checkPropertyClusterLabelKey } from './eg-maps-utils';

import { DestinationInput, PaginationInput } from 'src/common/__generated__/api';
import { EGMapConfiguration, FormatFunction } from '@eg-maps/core';

interface DynamicSearchMapWrapperProps {
  context?: ContextStore;
  searchCriteria?: SearchCriteriaModel;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  cfg?: EGMapConfiguration;
  clientPadding?: string;
  onMapDialogClosed?: () => void;
  filterComponent?: React.ReactNode;
  triggerRef?: React.Ref<HTMLElement>;
}

const { allKeys, flattenFields, withoutKeys } = merge;

export const DynamicSearchMapWrapper: React.FC<DynamicSearchMapWrapperProps> = inject(
  'context',
  'searchCriteria',
  'multiItem',
  'searchResults',
  'experiment',
  'analytics',
  'currentHotel'
)(
  observer(
    ({
      context,
      searchCriteria,
      multiItem,
      searchResults,
      experiment,
      analytics,
      currentHotel,
      cfg,
      clientPadding,
      onMapDialogClosed,
      filterComponent,
      triggerRef,
    }) => {
      const history = useHistory();
      const location = useLocation();
      const l10n = useLocalization();

      if (!context || !searchResults?.searchCriteria) return null;

      const calculateMapInputs = React.useCallback(() => {
        const useSharedUIListingsQuery =
          !multiItem?.isPackageShoppingPath() && experiment?.exposures?.EG_Property_Search_Query_Migration?.bucket === 1;
        const paginationPreviousSubset = useSharedUIListingsQuery
          ? searchCriteria?.paginationSubSets?.previousSubset
          : searchResults?.pagination?.subSets?.previousSubset;
        const paginationInput: PaginationInput = {
          size: paginationPreviousSubset?.size,
          startingIndex: (paginationPreviousSubset?.startingIndex ?? 0) + (paginationPreviousSubset?.size ?? 0),
        };

        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          context: bexApiContext,
          ...rest
        } = getPropertySearchInput(context, searchResults?.searchCriteria, multiItem?.multiItemContext, paginationInput);

        return rest;
      }, [context, multiItem, searchResults, searchCriteria, experiment]);

      const [mapInputs, setMapInputs] = React.useState(calculateMapInputs());

      React.useEffect(() => {
        autorun(() => {
          const newState = calculateMapInputs();
          setMapInputs(prevState => {
            const [prevStateWithoutDestination, newStateWithoutDestination] = [
              withoutKeys(untrackedDestinationKeys).merge({}, flattenFields(prevState)),
              withoutKeys(untrackedDestinationKeys).merge({}, flattenFields(newState)),
            ];
            const statesAreNotEqual = !isEqual(prevStateWithoutDestination, newStateWithoutDestination);
            const newStateHasSearchId = newState.criteria?.secondary?.selections?.find(selection => selection.id === 'searchId');
            if (statesAreNotEqual && newStateHasSearchId) {
              return newState;
            }

            const [prevDestination, newDestination] = [prevState?.criteria?.primary.destination, newState.criteria?.primary.destination];
            const coordsAreEqual = comparer.structural(
              allKeys().merge({}, prevDestination?.coordinates),
              allKeys().merge({}, newDestination?.coordinates)
            );
            const regionNamesAreEqual = prevDestination?.regionName === newDestination?.regionName;
            const newRegionId = newState.criteria?.primary?.destination?.regionId;

            /*
              1)We only want to initialize a render whenever the regionId is calulated by property listing
              to prevent the map from re-rendering twice on map pan.

              2)Due to the way dialog context and the universal search form compete to update the URL we should only render
              the map whenever we see that the regionNames are different; preventing the double render of the map caused by the universal search form's destination input changing.
            */

            if ((coordsAreEqual && newRegionId !== null) || (coordsAreEqual && !regionNamesAreEqual)) {
              return prevState;
            }

            return newStateHasSearchId ? newState : prevState;
          });
        });
      }, [context, multiItem, searchResults, searchCriteria, experiment]);

      const trackMapSearch: () => void = analytics?.clickTracker
        ? analytics?.clickTracker('Module.Map.Search.Button', 'User clicking the map search button on PWA HSR Map', null, () => null)
        : () => null;

      const onDestinationChanged = (destination: DestinationInput) => {
        const latLong = destination.coordinates
          ? `${destination.coordinates.latitude},${destination.coordinates.longitude}`
          : 'undefined,undefined';
        const mapBounds = destination.mapBounds
          ? [
              [destination.mapBounds[0].latitude, destination.mapBounds[0].longitude],
              [destination.mapBounds[1].latitude, destination.mapBounds[1].longitude],
            ]
          : null;
        trackMapSearch();

        updateSearch({
          history,
          location,
          newParams: {
            regionId: '',
            destination: '',
            selected: '',
            poi: '',
            latLong,
            // developer note, since this bernie function only accepts strings we would need to keep the ts-ignore here
            // to resolve type issues when querying with the mapBounds
            // @ts-ignore
            mapBounds,
            neighborhood: '',
          },
        });
        window.scrollTo(0, 0);
      };

      const translate: FormatFunction = React.useCallback((key, args) => checkPropertyClusterLabelKey(key, l10n, ...args), [l10n]);

      const egMapsConfig: EGMapConfiguration = React.useMemo(
        () =>
          ({
            ...cfg,
            i18n: {
              formatFunction: translate,
            },
          } as EGMapConfiguration),
        [cfg, translate]
      );

      const fullscreenOptions = onMapDialogClosed
        ? {
            onClose: onMapDialogClosed,
            isDialogOpen: true,
            triggerRef: triggerRef ?? null,
          }
        : undefined;

      const batchKey = experiment?.exposures?.SHOPPING_PWA_BATCHING_SRP_QUERY?.bucket === 1 ? PROPERTY_LISTING_BATCH_KEY : undefined;

      return (
        <DynamicSearchMap
          batchKey={batchKey}
          inputs={mapInputs}
          selectedFeatureId={searchCriteria?.getHighlightedPropertyId}
          clientPadding={clientPadding}
          fullscreenOptions={fullscreenOptions}
          egMapsConfig={egMapsConfig}
          filterComponent={filterComponent}
          onDestinationChanged={onDestinationChanged}
          onLodgingCardClickCallback={propertyId => {
            currentHotel?.setPropertyListingIdAsCurrentHotel(propertyId as string);
          }}
          height={'100%'}
        />
      );
    }
  )
);

export const untrackedDestinationKeys = [
  'criteria_primary_destination_regionName',
  'criteria_primary_destination_regionId',
  'criteria_primary_destination_coordinates',
  'criteria_primary_destination_pinnedPropertyId',
  'criteria_primary_destination_propertyIds',
  'criteria_primary_destination_mapBounds',
  'criteria_primary_destination_coordinates_latitude',
  'criteria_primary_destination_coordinates_longitude',
];

DynamicSearchMapWrapper.displayName = 'DynamicSearchMapWrapper';
